import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageMeta from "@/components/base/PageMeta";
import SiteLayout from "@/components/feature/SiteLayout";
import ComicCard from "@/components/feature/ComicCard";
import FilterPanel from "@/pages/category/components/FilterPanel";
import {
  allCategories,
  allComics,
  defaultFilters,
  filterComics,
  getCategoryById,
} from "@/utils/comics";

const PAGE_SIZE = 8;

export default function CategoryPage() {
  const { categoryId = "all" } = useParams();
  const category = getCategoryById(categoryId);
  const isAll = categoryId === "all";
  const [filters, setFilters] = useState({ ...defaultFilters });
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    setFilters({ ...defaultFilters });
    setVisibleCount(PAGE_SIZE);
  }, [categoryId]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [filters]);

  const results = useMemo(
    () => filterComics(allComics, filters, isAll ? "all" : categoryId),
    [filters, isAll, categoryId]
  );

  const visible = results.slice(0, visibleCount);
  const hasMore = visibleCount < results.length;

  const categoryName = isAll ? "全部漫画" : category ? `${category.name}漫画` : "未知分类";
  const intro = isAll
    ? "汇集 JMComic 全部精选漫画，按题材、地区与连载状态自由组合筛选，快速找到今天想读的那一部。"
    : category?.description ?? "该分类暂时没有内容，可以返回全部分类继续浏览。";

  const activeFilterCount = [
    filters.tag !== "全部",
    filters.region !== "全部",
    filters.status !== "全部",
  ].filter(Boolean).length;

  if (!isAll && !category) {
    return (
      <SiteLayout>
        <PageMeta
          title="分类未找到 - JMComic漫画频道"
          description="未找到对应的漫画分类，请返回 JMComic 首页或浏览全部漫画分类。"
          keywords="JMComic,漫画分类,漫画平台"
          path={`/category/${categoryId}`}
        />
        <div className="mx-auto flex w-full max-w-[1240px] flex-col items-center px-4 py-20 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-700">
            <i className="ri-compass-discover-line text-3xl"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">分类未找到</h1>
          <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-500">
            你访问的分类可能已经调整。可以从下方题材重新选择，或直接浏览全部漫画。
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            {allCategories.map((item) => (
              <Link
                key={item.id}
                to={`/category/${item.id}`}
                className="rounded-full border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-600 hover:border-primary-300 hover:text-primary-700"
              >
                {item.name}
              </Link>
            ))}
          </div>
          <Link
            to="/category/all"
            className="mt-6 flex h-10 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 text-sm font-semibold text-background-50 hover:bg-primary-600"
          >
            浏览全部漫画
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <PageMeta
        title={`JMComic - ${isAll ? "全部漫画" : `${category?.name}漫画`} 漫画全集 | 精选漫画平台`}
        description={`浏览JMComic的${isAll ? "全部" : category?.name}漫画，丰富题材，多样风格，满足不同漫画爱好者需求。`}
        keywords={`JMComic,${isAll ? "全部漫画" : `${category?.name}漫画`},漫画分类,连载漫画,漫画推荐`}
        path={`/category/${categoryId}`}
      />

      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full max-w-[1240px] px-4 py-6 md:px-6 md:py-8">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="hover:text-primary-700">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/category/all" className="hover:text-primary-700">
              漫画分类
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">{isAll ? "全部漫画" : category?.name}</span>
          </nav>

          <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-4">
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                <i className={`${isAll ? "ri-layout-grid-line" : category?.icon} text-2xl`}></i>
              </span>
              <div>
                <h1 className="font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-3xl">
                  {categoryName}
                </h1>
                <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-foreground-600">{intro}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs text-foreground-600">
                共 <strong className="font-heading text-sm text-foreground-950">{results.length}</strong> 部作品
              </span>
              <span className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs text-foreground-600">
                连载 <strong className="font-heading text-sm text-foreground-950">{results.filter((item) => item.status === "连载").length}</strong> 部
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1240px] px-4 py-6 md:px-6 md:py-8">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <aside className="hidden lg:col-span-3 lg:block">
            <div className="sticky top-[84px] space-y-4">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <h2 className="mb-3 font-heading text-sm font-bold text-foreground-950">分类导航</h2>
                <ul className="space-y-1">
                  <li>
                    <Link
                      to="/category/all"
                      className={`flex items-center justify-between rounded-md px-2.5 py-2 text-xs font-medium transition-colors ${
                        isAll
                          ? "bg-primary-100 text-primary-800"
                          : "text-foreground-600 hover:bg-background-100"
                      }`}
                    >
                      全部漫画
                      <span className="text-[10px] text-foreground-400">{allComics.length}</span>
                    </Link>
                  </li>
                  {allCategories.map((item) => (
                    <li key={item.id}>
                      <Link
                        to={`/category/${item.id}`}
                        className={`flex items-center justify-between rounded-md px-2.5 py-2 text-xs font-medium transition-colors ${
                          categoryId === item.id
                            ? "bg-primary-100 text-primary-800"
                            : "text-foreground-600 hover:bg-background-100"
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <i className={`${item.icon} text-sm`}></i>
                          {item.name}
                        </span>
                        <span className="text-[10px] text-foreground-400">
                          {allComics.filter((comic) => comic.categoryId === item.id).length}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <FilterPanel
                  filters={filters}
                  onChange={setFilters}
                  onReset={() => setFilters({ ...defaultFilters })}
                />
              </div>
            </div>
          </aside>

          <div className="lg:col-span-9">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500">
                  筛选结果 <strong className="font-heading text-sm text-foreground-950">{results.length}</strong> 部
                </span>
                {filters.tag !== "全部" && (
                  <button
                    type="button"
                    onClick={() => setFilters({ ...filters, tag: "全部" })}
                    className="flex items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] text-secondary-800"
                  >
                    标签：{filters.tag}
                    <i className="ri-close-line"></i>
                  </button>
                )}
                {filters.region !== "全部" && (
                  <button
                    type="button"
                    onClick={() => setFilters({ ...filters, region: "全部" })}
                    className="flex items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] text-secondary-800"
                  >
                    地区：{filters.region}
                    <i className="ri-close-line"></i>
                  </button>
                )}
                {filters.status !== "全部" && (
                  <button
                    type="button"
                    onClick={() => setFilters({ ...filters, status: "全部" })}
                    className="flex items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] text-secondary-800"
                  >
                    状态：{filters.status}
                    <i className="ri-close-line"></i>
                  </button>
                )}
              </div>

              <div className="flex items-center gap-2">
                <span className="hidden text-[11px] text-foreground-400 sm:block">
                  排序：{filters.sort}
                </span>
                <button
                  type="button"
                  onClick={() => setSheetOpen(true)}
                  className="flex h-10 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 text-sm font-medium text-foreground-700 lg:hidden"
                >
                  <i className="ri-filter-3-line text-base"></i>
                  筛选
                  {activeFilterCount > 0 && (
                    <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-500 px-1 text-[10px] text-background-50">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {visible.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-4 py-16 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
                  <i className="ri-search-eye-line text-2xl"></i>
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                  没有符合条件的漫画
                </h3>
                <p className="mt-1.5 max-w-sm text-sm text-foreground-500">
                  试试减少筛选条件，或者切换到其他题材标签再找找看。
                </p>
                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => setFilters({ ...defaultFilters })}
                    className="flex h-10 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 text-sm font-semibold text-background-50 hover:bg-primary-600"
                  >
                    <i className="ri-refresh-line"></i>
                    重置筛选
                  </button>
                  <Link
                    to="/"
                    className="flex h-10 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 text-sm font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                  >
                    返回首页
                  </Link>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-3">
                  {visible.map((comic) => (
                    <ComicCard key={comic.id} comic={comic} />
                  ))}
                </div>

                <div className="mt-6 flex flex-col items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
                    disabled={!hasMore}
                    className={`flex h-11 items-center gap-1.5 whitespace-nowrap rounded-md px-6 text-sm font-semibold transition-colors ${
                      hasMore
                        ? "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                        : "cursor-not-allowed border border-background-200 bg-background-100 text-foreground-400"
                    }`}
                  >
                    {hasMore ? (
                      <>
                        加载更多
                        <i className="ri-arrow-down-line"></i>
                      </>
                    ) : (
                      "已经到底啦"
                    )}
                  </button>
                  <p className="text-[11px] text-foreground-400">
                    已显示 {visible.length} / {results.length} 部作品
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {sheetOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setSheetOpen(false)}
            className="absolute inset-0 bg-foreground-950/50"
          ></button>
          <div className="animate-rise absolute inset-x-0 bottom-0 max-h-[82vh] overflow-y-auto rounded-t-lg border-t border-background-200 bg-background-50 p-5 pb-24">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-heading text-base font-bold text-foreground-950">筛选漫画</h3>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-600"
                aria-label="关闭"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <FilterPanel
              filters={filters}
              onChange={setFilters}
              onReset={() => setFilters({ ...defaultFilters })}
            />
            <div className="mt-6 flex gap-2">
              <button
                type="button"
                onClick={() => setFilters({ ...defaultFilters })}
                className="h-11 flex-1 whitespace-nowrap rounded-md border border-background-300 text-sm font-medium text-foreground-700"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="h-11 flex-1 whitespace-nowrap rounded-md bg-primary-500 text-sm font-semibold text-background-50"
              >
                查看 {results.length} 部结果
              </button>
            </div>
          </div>
        </div>
      )}
    </SiteLayout>
  );
}